import { Button } from '@progress/kendo-react-buttons';
import { Card } from 'app/components/Card';
import { Divider } from 'app/components/Divider';
import { SubscriptionInfo } from 'app/hooks/subscriptions/useSubscriptionInfo';
import { getDefaultThumbnail } from 'app/pages/Equipment/utils';
import clsx from 'clsx';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { IEquipmentEntry } from 'types/IEquipmentEntry';
import './SubscriptionInfoCard.less';
import { InitialPlanEndsOn } from './components';
import { GlobalErrorContext } from 'app/components/ReactContexts/globalErrorContext';
import { useFullscreenStripeLoader } from 'context';
import { subscriptionsApi } from 'services/api/subscription/queries';

interface Props {
  equipment: IEquipmentEntry;
  subscriptionInfo: SubscriptionInfo;
  classname?: string;
}

export default function SubscriptionInfoCard({
  equipment,
  subscriptionInfo,
  classname,
}: Props) {
  const { t } = useTranslation();

  const { addErrorModal } = useContext(GlobalErrorContext);

  const [
    createCustomerPortalSession,
    { isLoading: isCreatingCustomerPortalSession },
  ] = subscriptionsApi.useCreateCustomerPortalSessionMutation();

  const { show: showStripeLoader, hide: hideStripeLoader } =
    useFullscreenStripeLoader();

  const defaultThumbnail = getDefaultThumbnail(
    equipment.type,
    equipment.subcategory,
  );
  const modelIconUrl = equipment.modelIconUrl || defaultThumbnail;

  function handleImageError(
    event: React.SyntheticEvent<HTMLImageElement, Event>,
  ) {
    event.currentTarget.onerror = null;
    event.currentTarget.src = defaultThumbnail;
  }

  async function handleOpenCustomerPortal() {
    showStripeLoader();

    try {
      const { sessionUrl } = await createCustomerPortalSession({
        returnUrl: window.location.href,
      }).unwrap();

      window.location.href = sessionUrl;
    } catch (error) {
      hideStripeLoader();
      addErrorModal();
    }
  }

  function renderContent() {
    if (subscriptionInfo.isInitial) {
      return (
        <InitialPlanEndsOn
          endsOn={
            subscriptionInfo.rawEndsOn ?? subscriptionInfo.rawNextPaymentDate!
          }
          formattedEndsOn={
            subscriptionInfo.endsOn ?? subscriptionInfo.nextPaymentDate!
          }
        />
      );
    }

    return (
      <>
        <span className="subscription-info-card__premium_expiration_info">
          {t(
            `subscriptions.manage_your_plan.premium_telematics_plan.${
              subscriptionInfo.shouldShowExpiration
                ? 'expires_on'
                : 'next_payment'
            }`,
          )}{' '}
          <span className="subscription-info-card__expiration">
            {subscriptionInfo.shouldShowExpiration
              ? subscriptionInfo.endsOn
              : subscriptionInfo.nextPaymentDate}
          </span>
        </span>

        <Divider />

        <Button
          themeColor="dark"
          className="subscription-info-card__manage-subscription-button"
          onClick={handleOpenCustomerPortal}
          disabled={isCreatingCustomerPortalSession}
        >
          {t(
            'subscriptions.manage_your_plan.premium_telematics_plan.manage_subscription_button',
          )}
        </Button>
      </>
    );
  }

  return (
    <Card className={clsx('subscription-info-card', classname)}>
      <div className="subscription-info-card__equipment-details">
        <div className="subscription-info-card__plan-header">
          <h3 className="subscription-info-card__plan-title">
            {t(
              `subscriptions.manage_your_plan.${
                subscriptionInfo.isInitial
                  ? 'initial_premium_telematics_plan'
                  : 'premium_telematics_plan'
              }.title`,
            )}
          </h3>

          <div className="subscription-info-card__details">
            <span className="subscription-info-card__details-label">
              {t('equipment_model')}
            </span>
            <span className="subscription-info-card__details-value">
              {equipment.model}
            </span>

            <span className="subscription-info-card__details-label">
              {equipment.identifierType === 'Pin'
                ? t('equipment_pin')
                : t('equipment_serial')}
            </span>
            <span
              className="subscription-info-card__details-value"
              title={equipment.pinOrSerial}
            >
              {equipment.pinOrSerial}
            </span>
          </div>
        </div>

        <img
          src={modelIconUrl}
          alt="Equipment"
          className="subscription-info-card__plan-image"
          onError={handleImageError}
        />
      </div>

      <Divider />

      {renderContent()}
    </Card>
  );
}
